import * as Sentry from '@sentry/browser'

import { agentV2Request } from '@/agent-v2/conversations/fetch/agent-v2-request'
import { AgentV2TurnAdmissionResponse, AgentV2TurnAdmissionResponseSchema, AgentV2TurnRequestPayload } from '@/agent-v2/store/schemas'
import { kAgentV2RouterApiBasePath } from '@/constants/constants-api-paths'
import { kPaxtonAppApiRESTBaseUrl } from '@/constants/constants-links'
import { getResponseDebugContext, getTopLevelKeys } from '@/util/api/response-body'
import { ApiResponsePayloadError } from '@/util/errors/api-response-payload-error'

type PutAgentV2TurnRequestArgs = {
  clientRequestId: string
  conversationId: string
  request: AgentV2TurnRequestPayload
}

export async function putAgentV2TurnRequest(args: PutAgentV2TurnRequestArgs): Promise<AgentV2TurnAdmissionResponse> {
  const url = `${kPaxtonAppApiRESTBaseUrl()}${kAgentV2RouterApiBasePath}/conversations/${encodeURIComponent(
    args.conversationId
  )}/turn-requests/${encodeURIComponent(args.clientRequestId)}`

  return agentV2Request({
    url,
    method: 'PUT',
    sentryEndpoint: 'PUT /api/v2/agent/conversations/{conversation_id}/turn-requests/{client_request_id}',
    sentryExtra: {
      clientRequestId: args.clientRequestId,
      conversationId: args.conversationId,
    },
    body: JSON.stringify(args.request),
    parseResponse: (data, { response, sentryTags }) => {
      const parsed = AgentV2TurnAdmissionResponseSchema.safeParse(data)
      if (parsed.success && parsed.data.client_request_id === args.clientRequestId) return parsed.data
      const issues = parsed.success ? [{ message: 'client_request_id does not match the requested admission' }] : parsed.error.issues

      Sentry.withScope((scope) => {
        scope.setTags(sentryTags)
        Sentry.captureException(new Error('Agent v2 turn admission response schema validation failed'), {
          extra: {
            ...getResponseDebugContext(response, url),
            clientRequestId: args.clientRequestId,
            conversationId: args.conversationId,
            responseKeys: getTopLevelKeys(data),
            issues,
          },
        })
      })

      throw new ApiResponsePayloadError(response.status, 'Invalid Agent v2 turn admission response shape', issues)
    },
  })
}
